import { config } from '@/config';
import { countries } from '@/consts/languageAccordance';
import { getErrorMessage } from './getErrorMessage';
import { isArrayWithValues } from './fp';
import { getCurrentLocaleData } from './getCurrentLocaleData';
import { isObject } from '@/types';
import { createCache } from './fetch/Cache/createCache';

interface IGetPhrases {
    tagIds: number | number[];
    locale?: string;
    path?: string;
    phraseIds?: number | number[];
    subpanelName?: string;
    cacheType?: 'nextRevalidate' | 'custom';
}

const { get, set } = createCache<Record<string, string>>({
    revalidate: config.timeout.shortRevalidate,
});

export const getPhrases = async ({
    tagIds,
    locale,
    path,
    phraseIds,
    subpanelName,
    cacheType = 'nextRevalidate',
}: IGetPhrases) => {
    const shouldUseCustomCache = cacheType === 'custom';
    const url = buildUrl(locale, path, tagIds, phraseIds, subpanelName);
    const cacheKey = url.toString();

    const tags = isArrayWithValues(tagIds)
        ? [...tagIds.map((item) => `phrases/${item}`), 'phrases']
        : [`phrases/${tagIds}`, 'phrases'];

    try {
        const fetchOptions: RequestInit = shouldUseCustomCache
            ? { cache: 'no-store' }
            : {
                  next: {
                      tags,
                      revalidate: config.timeout.revalidate,
                  },
              };

        if (shouldUseCustomCache && get(cacheKey)) {
            return get(cacheKey);
        }

        const res = await fetch(url, fetchOptions);

        const json: unknown = await res.json();
        const isValidPhrases = (v: unknown): v is { data: Record<string, string> } =>
            isObject(v) && 'data' in v && isObject(v.data);

        if (isValidPhrases(json)) {
            if (shouldUseCustomCache) {
                set(cacheKey, json.data);
            }
            return json.data;
        } else {
            const errorMessage = `Received phrases with incorrect data at ${
                url.href
            }: ${JSON.stringify(json)}`;
            const err = new Error(errorMessage);
            err.name = 'incorrect data';
            throw err;
        }
    } catch (err) {
        const errorMessage = `Unable to fetch phrases via ${url.href}: ${getErrorMessage(err)}`;
        if (err && isObject(err) && 'message' in err && err?.name !== 'incorrect data') {
            err.message = errorMessage;
        }
        throw err;
    }
};

function buildUrl(
    locale: string | undefined,
    path: string | undefined,
    tagIds: number | number[],
    phraseIds: number | number[] | undefined,
    subpanelName: string | undefined | null,
): URL {
    const { languageAttribute, country, countryAttribute } = getCurrentLocaleData(locale);
    const clientLocation = `${config.basePath}${path ?? ''}`;
    const url = new URL(config.api.url.translate);
    appendArrayParams(tagIds, 'tagIds', url);
    url.searchParams.append('clientLocation', clientLocation);
    url.searchParams.append('attributes', languageAttribute);
    if (country && country !== countries.int.name.en) {
        url.searchParams.append('attributes', countryAttribute);
    }
    appendArrayParams(phraseIds, 'phraseIds', url);
    if (subpanelName) {
        url.searchParams.append('attributes', `subpanel|${subpanelName}`);
    }
    return url;
}

function appendArrayParams(
    data: number | string | number[] | string[] | undefined,
    tagName: string,
    url: URL,
) {
    if (!data) return;
    if (isArrayWithValues(data)) {
        data.forEach((item) => {
            url.searchParams.append(tagName, String(item));
        });
    } else {
        url.searchParams.append(tagName, String(data));
    }
}
